import { configureStore, combineReducers } from "@reduxjs/toolkit";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import { setupListeners } from "@reduxjs/toolkit/query";
import globalReducer from "@/state";
import { api } from "@/state/api";

const rootReducer = combineReducers({
  global: globalReducer,
  [api.reducerPath]: api.reducer,
});

const persistConfig = {
  key: "root",
  version: 1,
  storage,
  whitelist: ["global"],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  devTools: process.env.NODE_ENV !== "production",
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        ignoredActionPaths: [
          "meta.arg.originalArgs.file",
          "meta.arg.originalArgs.formData",
          "payload.chapter.video",
          "meta.baseQueryMeta.request",
          "meta.baseQueryMeta.response",
        ],
        ignoredPaths: [
          "global.courseEditor.sections",
          "entities.videos.data",
          "meta.baseQueryMeta.request",
          "meta.baseQueryMeta.response",
        ],
      },
    }).concat(api.middleware),
});

setupListeners(store.dispatch);

// A request that never reached the server (restart, dropped connection, timeout)
// or that blew up inside it is NOT an authentication failure - the session is
// still perfectly good. Only a real 4xx from the API means "you are logged out".
const isTransportError = (result: any): boolean => {
  const status = result?.error?.status;
  if (status === undefined || status === null) return false;
  if (typeof status === "number") return status >= 500;
  return (
    status === "FETCH_ERROR" ||
    status === "TIMEOUT_ERROR" ||
    status === "PARSING_ERROR"
  );
};

export const initializeApp = async () => {
  let refreshResult = await store.dispatch(
    api.endpoints.refreshToken.initiate({}, { forceRefetch: true })
  );
  let refresh2ndTime;
  if (refreshResult?.isError) {
    refresh2ndTime = await store.dispatch(
      api.endpoints.refreshToken.initiate({}, { forceRefetch: true })
    );
  }

  const user = await store.dispatch(
    api.endpoints.loadUser.initiate({}, { forceRefetch: true })
  );
  if (user?.isError) {
    let user2ndTime = await store.dispatch(
      api.endpoints.loadUser.initiate({}, { forceRefetch: true })
    );
    if (refresh2ndTime?.isError) {
      return {
        isError: true,
        isTransportError:
          isTransportError(refresh2ndTime) || isTransportError(user2ndTime),
      };
    }
    if (user2ndTime?.isError) {
      return { isError: true, isTransportError: isTransportError(user2ndTime) };
    }
  }
  return { isError: false, isTransportError: false };
};

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
